import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { readCkScope, scopeKey, useCkScope } from './CkOperationsShared';

/**
 * Missed safety checks (SAF01).
 *
 * Reports the specific scheduled check that should have happened and did not.
 * A missed check is *missing*, never *failed* — the underlying condition is
 * unknown and the surface must not imply otherwise. No compliance percentage is
 * shown, because SAF01 forbids an unsupported one.
 */
export default function MissedChecks() {
  const scope = useCkScope();
  return <Surface key={scopeKey(scope)} scope={scope} />;
}

function Surface({ scope }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const current = readCkScope();
      const res = await fetch(`${apiBase()}/api/mobile/safety/missed-checks?lookback_days=14`, {
        headers: authHeaders(current),
      });
      if (res.status === 403) {
        setError('Your role cannot view missed safety checks.');
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = await res.json();
      if (alive.current) setData(body);
    } catch (e) {
      if (alive.current) setError(e.message || 'Could not load missed checks');
    } finally {
      if (alive.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  return (
    <main aria-label="Missed safety checks">
      <header>
        <h2>Missed safety checks</h2>
        <button onClick={load} disabled={busy} aria-label="Refresh"><RefreshCw size={16} /></button>
      </header>

      {error && <p role="alert" className="wf-error">{error}</p>}

      {data && (
        <>
          <p>
            {data.missed_count} missed, {data.due_count} still due, {data.submitted_count} submitted
            over the last {data.lookback_days} days.
          </p>

          {/* SAF01 forbids an unsupported compliance percentage; say why none is shown. */}
          <p role="status" className="wf-notice" data-testid="no-compliance-rate">
            No compliance percentage is shown. {data.no_compliance_rate_reason}
          </p>
          <p className="wf-muted" data-testid="missed-not-failed">{data.notice}</p>

          {data.missed_count === 0
            ? <p role="status"><ShieldCheck size={16} /> Nothing is overdue in this window.</p>
            : (
              <ul>
                {data.missed.map((c) => (
                  <li key={`${c.checklist_id}:${c.due_for}`} data-risk={c.risk_level}>
                    <AlertTriangle size={14} />{' '}
                    <strong>{c.name}</strong> — {c.checklist_type} due {c.due_for}
                    <small> (window closed {c.due_by})</small>
                  </li>
                ))}
              </ul>
            )}

          {data.missed_by_risk && Object.keys(data.missed_by_risk).length > 0 && (
            <section aria-label="Missed by risk">
              <h3>Missed by risk</h3>
              <ul>
                {Object.entries(data.missed_by_risk).map(([level, count]) => (
                  <li key={level}>{level}: {count}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </main>
  );
}
